import { defineStore } from 'pinia'
import { isAxiosError } from 'axios'

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { until, useDebounceFn } from '@vueuse/core'
import { klona } from 'klona'
import { uuid } from '@dashboard/services/uuid'
import api from '@dashboard/services/axiosApi'
import type { Config } from '@dashboard/types/config'
import type { HostedPublishOutcome } from '@dashboard/modules/pages/services/hostedApi'
import { applyModifiedAtStamps } from '@dashboard/utils/modifiedAt'
import { setProjectTimezone } from '@dashboard/utils/projectTimezone'
import type { Team } from '@dashboard/types/team'
import type { User } from '@dashboard/types/user'
import { AnalyticsEventTypes, recordAnalyticsEvent } from '@dashboard/services/analytics'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { useRightyStore } from '@dashboard/modules/righty/stores/rightyStore'
import { repairQuestions, repairFlowSplits } from '@dashboard/modules/segment/services/segmentActions'
import { useConfigChanges } from '@dashboard/composables/useConfigChanges'
import { flushPendingDebouncedFields, hasPendingDebouncedFields } from '@dashboard/composables/useDebouncedField'
import {
  isValidTurnstileSiteKey,
  isValidTurnstileSecretKey
} from '@dashboard/modules/settings/services/settingsUtilities'
import {
  createRemoteRefetchCoordinator,
  isOwnDraftConfigEvent,
  isOwnRightyDraftConfigEvent,
  resolveStaleOrForeignChange,
  shouldAutosaveConfigChange,
  shouldDeferRemoteRefetch,
  shouldRequestRemoteRefetch,
  usableChecksum,
  type DraftConfigUpdatedPayload
} from './configAutosaveSync'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { classifyOfferPublishability, offerUsedBy } from '@dashboard/modules/offer/services/offerUtilities'
import { useOfferStore } from '@dashboard/modules/offer/stores/offerStore'
import { useWidgetStore } from '@dashboard/modules/widget/stores/widgetStore'
import { computed, nextTick, ref, toRaw, watch } from 'vue'
import { useAuthStore } from '@dashboard/stores/authStore'
import { fetchHostedSite } from '@dashboard/modules/pages/services/hostedApi'

export enum PublishState {
  Loading = 0,
  Published = 1,
  Dirty = 2,
  Saving = 3,
  Saved = 4
}

type PublishValidationError = {
  message: string
  references: string[]
}

export type PublishResponse = { hosted: HostedPublishOutcome }

/**
 * A publish the edge cut off (502/504) or whose response never arrived. The server
 * keeps publishing, so this is not a failure; `publishConfigMutation` reports it.
 */
export const isPublishStillRunningError = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false
  }
  const status = error.response?.status
  return status === undefined || status === 502 || status === 504
}

// Initialized inside defineStore's setup function on first store access (line below).
// Typed as `string | undefined` because callers may invoke the exposed tabId getter
// before any component has mounted the store (rare, but possible in non-component
// contexts like background composables) — see useRightyInline.ts for the guarded read.
let tabId: string | undefined
type TeamMember = {
  id: number
  name: string | null
  email: string
  role: string
  invited: boolean
  is_agency: boolean
  agency_name: string | null
}

type CurrentConfigResponse = {
  id: string
  draft: Config | null
  stable: Config | null
  checksum: string
  // The stable row is still being published (queued within the last minute).
  publishing?: boolean
  publishedConfigId?: number | null
  team: Team
  members: TeamMember[]
  owner: User
  teams: Team[]
}

export const useConfigStore = defineStore('config', () => {
  tabId = uuid()
  const { hasChanges: hasConfigChanges } = useConfigChanges()
  const authStore = useAuthStore()
  // Whether teammates' and MCP changes refetch into this editor (Preferences › Live updates).
  const liveUpdatesEnabled = computed(() => authStore.user?.live_updates_enabled !== false)

  const fullResponse = ref<CurrentConfigResponse | null>(null)
  const currentTeamPid = ref<number | 'demo' | null>(null)
  const config = ref<Config | null>(null)
  const snapshots = ref<[]>([])
  const teamMembers = ref<TeamMember[]>([])
  const currentTeam = ref<Team | null>(null)
  watch(() => currentTeam.value?.timezone, setProjectTimezone, { immediate: true })
  const lastSavedConfig = ref<Config | null>(null)
  const lastChecksum = ref<string | null>(null)
  const publishState = ref<PublishState>(PublishState.Loading)
  const savesMade = ref(0)
  const isDirty = ref(false)
  const lastMeaningfulSaveTime = ref(0) // Timestamp of last save with actual changes
  // True while the config (re)fetch watcher is adopting a server/MCP config
  // wholesale. Gates the deep-watch autosave so the adopted server state isn't
  // immediately bounced back as a "local edit". Cleared via nextTick so the
  // flag is still set when Vue flushes the async deep watcher triggered by the
  // config assignment. Replaces the old three-way-merge `isMerging` guard —
  // there is no merge anymore, just wholesale adoption. (ENG-1066)
  const isApplyingRemoteConfig = ref(false)
  // True while currentTeam is being replaced wholesale from a server fetch or
  // team-creation seed. The teamStore deep watch only auto-saves in-place user
  // edits; bouncing a freshly-fetched / freshly-created shell back to the API
  // can clobber richer server state like ai_context that lands moments later.
  const isApplyingRemoteTeam = ref(false)
  // True while a passive, non-user normalization runs (repairSegmentGroup on
  // opening the question/segment-group editor, load-time repairs). Gates the
  // deep-watch autosave so merely viewing a question can't queue a spurious
  // unpublished change. (ENG-1063)
  const isNormalizing = ref(false)
  const isLocalEditActive = ref(false)
  let saveSeq = 0 // Monotonically increasing save sequence to handle out-of-order responses
  let localEditIdleTimer: ReturnType<typeof setTimeout> | null = null
  // Guards the sticky "project changed elsewhere — reload" prompt so the 2s
  // autosave debounce and repeated foreign DraftConfigUpdated events don't spam
  // it. Reset once we successfully adopt the server state (refetch lands).
  let remoteChangePromptShown = false
  let remoteChangeSource: 'righty' | 'foreign' = 'foreign'
  let savingCurrentDraft: Promise<boolean> | null = null
  let rightyRefetch: Promise<void> | null = null
  let rightyRefetchRequested = false

  const queryClient = useQueryClient()

  const notifyDraftConfigEvent = (data: DraftConfigUpdatedPayload) => {
    if (!data.message) {
      return
    }

    const notificationStore = useNotificationStore()
    notificationStore.addNotification(data.message)
  }

  // "Do I have edits the server doesn't have yet, that a refetch would
  // destroy?" Reuses the store's existing meaningful-change detector
  // (jsondiffpatch-based, ignores accessedAt-only churn) rather than a shallow
  // string compare, so phantom diffs don't make us think we have edits to lose
  // (or vice-versa). When config isn't loaded yet there is nothing to lose.
  // The load-time repairs (repairFlowSplits sorts dimensions, repairQuestions
  // backfills flags) also differ from the server copy, but a refetch re-runs
  // them, so the repaired adoption is not an edit to protect. Counting it held
  // every later Righty refetch once a turn added a router to the flow.
  let repairedAdoption: Config | null = null
  const hasUnsavedLocalChanges = () => {
    if (!config.value || !lastSavedConfig.value) {
      return false
    }
    return (
      hasConfigChanges(config.value, lastSavedConfig.value) &&
      (repairedAdoption === null || hasConfigChanges(config.value, repairedAdoption))
    )
  }

  // True only for the tick where we synchronously flush buffered field edits
  // into the config tree before handling a foreign/stale server update. The
  // deep config watcher runs asynchronously, so this bridges the gap between
  // `flushPendingDebouncedFields()` and `hasUnsavedLocalChanges()` becoming
  // observably true.
  let isFlushingBufferedLocalEdits = false

  const hasLocalEditsToProtect = () =>
    hasPendingDebouncedFields() || isFlushingBufferedLocalEdits || hasUnsavedLocalChanges()

  const flushBufferedLocalEdits = () => {
    if (!flushPendingDebouncedFields()) {
      return
    }

    isFlushingBufferedLocalEdits = true
    void nextTick(() => {
      isFlushingBufferedLocalEdits = false
      remoteRefetch.flush()
    })
  }

  // Non-blocking, sticky notification telling the user the project changed
  // elsewhere (MCP/Righty/another tab) while they had unsaved local edits.
  // We never auto-merge or silently overwrite either side — the user reloads
  // when ready (their unsaved local edits are replaced on reload; this is the
  // acknowledged no-OT/CRDT boundary). Shown once per pending foreign change.
  const promptRemoteChange = () => {
    if (remoteChangePromptShown) {
      return
    }
    remoteChangePromptShown = true
    const notificationStore = useNotificationStore()
    notificationStore.addError(
      remoteChangeSource === 'righty'
        ? 'Righty updated this draft while you were editing. Your unsaved edits have been kept on this screen. Reload to see Righty’s changes; reloading will replace those unsaved edits.'
        : 'This project was changed elsewhere (via Claude/MCP or another tab) while you had unsaved changes here. Reload the page to see the latest version — your unsaved changes on this screen will be replaced when you reload.',
      true,
      { group: 'config-reload' }
    )
  }

  const shouldHoldRemoteRefetch = () =>
    shouldDeferRemoteRefetch({
      hasUnsavedLocalChanges: hasLocalEditsToProtect(),
      isLocalEditActive: isLocalEditActive.value,
      isSaving: isUpdatingConfig.value
    })

  const markLocalEditActive = () => {
    isLocalEditActive.value = true
    if (localEditIdleTimer) {
      clearTimeout(localEditIdleTimer)
    }

    localEditIdleTimer = setTimeout(() => {
      isLocalEditActive.value = false
      remoteRefetch.flush()
    }, 2500)
  }

  // Run a passive, non-user config normalization (repairSegmentGroup on
  // opening the editor, load-time repairs) without it being treated as a
  // user edit. The flag is cleared on nextTick — NOT synchronously — so it is
  // still set when Vue flushes the async deep watcher triggered by the
  // mutation. This mirrors the isApplyingRemoteConfig guard's nextTick clear
  // so merely viewing a question produces zero draft changes (ENG-1063). The
  // in-memory repair still happens; it just isn't auto-persisted on a clean
  // open — it rides along the next real user edit instead.
  const runWithoutAutosave = (fn: () => void) => {
    isNormalizing.value = true
    try {
      fn()
    } finally {
      void nextTick(() => {
        isNormalizing.value = false
      })
    }
  }

  // Mirror runWithoutAutosave for the much smaller `currentTeam` object: mark
  // whole-object adoption from the server/store so the teamStore deep watch
  // does not POST that non-user state back to `/team`. Cleared on nextTick so
  // the async watch triggered by the assignment still sees the guard.
  const runWithoutTeamAutosave = (fn: () => void) => {
    isApplyingRemoteTeam.value = true
    try {
      fn()
    } finally {
      void nextTick(() => {
        isApplyingRemoteTeam.value = false
      })
    }
  }

  const configQuery = useQuery({
    queryKey: ['config', currentTeamPid],
    queryFn: () => api.get(`/api/${currentTeamPid.value}/configs/current`).then((res) => res.data),
    enabled: () => !!currentTeamPid.value,
    retry: false,
    refetchOnWindowFocus: false
  })

  const remoteRefetch = createRemoteRefetchCoordinator({
    shouldHold: shouldHoldRemoteRefetch,
    refetch: () => {
      void configQuery.refetch()
    }
  })

  // Caller-driven, coordinator-routed refetch for explicit local actions that
  // need to re-sync config (Righty streaming-end after MCP mutations, Righty
  // undo). The bare `configQuery.refetch()` bypasses the remoteRefetch
  // coordinator and silently discards local edits in the wholesale-adoption
  // watcher when the user typed during a Righty stream — the `isStreaming`
  // guard in `debouncedUpdateConfig` blocked the save, so
  // `hasUnsavedLocalChanges()` is still true on stream-end, but a raw refetch
  // adopts server state without checking. Routing through the coordinator
  // inherits the same flush → request → prompt dance the foreign WebSocket
  // event uses, so caller-driven refetches honor resolveStaleOrForeignChange
  // (no local edits → refetch + adopt; local edits → defer + sticky reload
  // prompt). (ENG-1066 review follow-up.)
  const requestRemoteRefetch = () => {
    flushBufferedLocalEdits()
    const refetched = remoteRefetch.request()
    if (!refetched && hasLocalEditsToProtect()) {
      promptRemoteChange()
    }
  }

  const requestRightyRefetch = (): Promise<void> => {
    rightyRefetchRequested = true
    if (rightyRefetch) {
      return rightyRefetch
    }
    if (remoteRefetch.isDeferred() && remoteChangeSource === 'foreign') {
      requestRemoteRefetch()
      return Promise.resolve()
    }
    rightyRefetch = (async () => {
      try {
        while (rightyRefetchRequested) {
          rightyRefetchRequested = false
          remoteChangeSource = 'righty'
          if (!(await saveCurrentDraft())) {
            remoteRefetch.request()
            promptRemoteChange()
            return
          }
          remoteRefetch.cancel()
          await configQuery.refetch()
        }
      } catch {
        remoteRefetch.request()
      } finally {
        rightyRefetch = null
      }
    })()
    return rightyRefetch
  }

  watch(currentTeamPid, (newVal, oldVal) => {
    // Skip Echo connections in demo mode
    if (oldVal && oldVal !== 'demo') {
      window.Echo?.leave(`config-changes.${oldVal}`)
    }
    if (newVal && newVal !== 'demo') {
      window.Echo?.private(`config-changes.${newVal}`).listen(
        'DraftConfigUpdated',
        (data: DraftConfigUpdatedPayload) => {
          if (isOwnRightyDraftConfigEvent(data, tabId)) {
            void requestRightyRefetch()
            notifyDraftConfigEvent(data)
            return
          }
          // Skip refetch for our own saves — the self-echo would clobber
          // any changes the user made between the debounce firing and the
          // WebSocket round-trip completing.
          if (isOwnDraftConfigEvent(data, tabId)) {
            const checksum = usableChecksum(data.checksum)
            if (checksum) {
              lastChecksum.value = checksum
            }
            notifyDraftConfigEvent(data)
            return
          }
          // Personal opt-out (Preferences › Live updates): people who work
          // alone can turn off websocket-driven refetches to avoid the save
          // race that can wipe in-progress edits. It defaults to on.
          const autoRefreshEnabled = liveUpdatesEnabled.value
          if (
            shouldRequestRemoteRefetch({
              checksum: data.checksum,
              lastChecksum: lastChecksum.value,
              autoRefreshEnabled
            })
          ) {
            // Foreign change (MCP/Righty/another tab).
            //  - not held  → refetch now and adopt server/MCP state wholesale
            //    (nothing local to lose). Fixes the dominant data-loss report.
            //  - held back → the parked refetch stays until idle/clean. Only
            //    surface the reload prompt when there are GENUINE unsaved
            //    local edits to lose (resolveStaleOrForeignChange). A hold
            //    caused purely by isLocalEditActive (e.g. the 2.5s window
            //    after initial load) or an in-flight save has nothing to
            //    lose — the refetch flushes itself once idle. No merge.
            //    (ENG-1066)
            // Before deciding whether there are local edits to protect, commit
            // any in-flight buffered field edits into the config tree. These
            // local buffers intentionally do not update config on every
            // keystroke; without this flush a foreign refetch can adopt server
            // state while a pending input later flushes and overwrites it.
            flushBufferedLocalEdits()
            remoteChangeSource = 'foreign'
            const refetched = remoteRefetch.request()
            if (!refetched && hasLocalEditsToProtect()) {
              promptRemoteChange()
            }
          } else if (!autoRefreshEnabled) {
            // Track the latest server checksum even when skipping the refetch,
            // so our next save sends the current baseline and isn't stale-rejected.
            remoteRefetch.cancel()
            const checksum = usableChecksum(data.checksum)
            if (checksum) {
              lastChecksum.value = checksum
            }
          }
          notifyDraftConfigEvent(data)
        }
      )
    }
  })

  const configAsString = computed(() => JSON.stringify(config.value))

  watch(liveUpdatesEnabled, (enabled) => {
    if (!enabled) {
      remoteRefetch.cancel()
    }
  })

  const owner = computed(() => fullResponse.value?.owner)

  const loadSnapshotMutation = useMutation({
    mutationFn: (configId: number) =>
      api.post(`/api/${currentTeamPid.value}/configs/load_snapshot/${configId}`).then((res) => res.data),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['snapshots'] })
    }
  })
  const deleteSnapshotMutation = useMutation({
    mutationFn: (configId: number) =>
      api.delete(`/api/${currentTeamPid.value}/configs/${configId}`).then((res) => res.data),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['snapshots'] })
    }
  })

  // Adopt server state wholesale and clear the guard on nextTick (so it is
  // still set when Vue flushes the async deep watcher triggered by the config
  // assignment). If a synchronous local edit raced the adoption into `config`
  // (the deep watcher skipped it because isApplyingRemoteConfig was set),
  // schedule its save so it isn't silently orphaned — it now rides on top of
  // the freshly-adopted server baseline + checksum, so it is not stale.
  // (ENG-1066; preserves the ENG-1024 raced-edit guarantee.)
  const finishApplyRemoteConfig = () => {
    isApplyingRemoteConfig.value = false
    if (config.value && hasUnsavedLocalChanges()) {
      void debouncedUpdateConfig(config.value)
    }
  }

  // Server config (re)fetch handler. This is an explicit `watch` on the
  // TanStack query data — NOT a watchEffect — so it fires ONLY when a real
  // fetch/refetch produces new server data, never when this handler mutates
  // `config`/`lastSavedConfig` itself. (The old three-way merge survived its
  // own writes via a jsondiffpatch fixpoint; wholesale adoption has no such
  // fixpoint, so we must not depend on the refs we write.) End-to-end the
  // model is now: local edits → debounced POST; own echo suppressed; foreign
  // echo → refetch+adopt (no local edits) or defer+prompt (local edits).
  // (ENG-1066)
  watch(
    () => configQuery.data.value,
    (data) => {
      if (!data) {
        return
      }

      const incoming = klona(data)
      const incomingConfig = incoming.draft || incoming.stable
      const isInitialLoad = !config.value || !lastSavedConfig.value
      if (!isInitialLoad && remoteChangeSource === 'righty' && hasLocalEditsToProtect()) {
        flushBufferedLocalEdits()
        remoteRefetch.request()
        promptRemoteChange()
        return
      }

      if (isInitialLoad) {
        // First load — nothing local yet.
        config.value = klona({ ...incomingConfig, id: incoming.id })
      } else {
        // A refetch landed. We only ever refetch when there were no unsaved
        // local edits to lose — the remote-refetch coordinator holds otherwise
        // (and post-publish refetch deliberately re-snapshots first). So adopt
        // the server/MCP state WHOLESALE. No client-side three-way merge: that
        // was the ENG-830/966 machinery removed in ENG-1066 because rebasing
        // two divergent JSON trees in the most critical path lost data in the
        // common case it was meant to protect.
        isApplyingRemoteConfig.value = true
        config.value = klona({ ...incomingConfig, id: incoming.id })
        // Server state adopted — any prior "changed elsewhere" prompt is now
        // resolved; allow a future foreign change to prompt again.
        remoteChangePromptShown = false
        remoteChangeSource = 'foreign'
        void nextTick(finishApplyRemoteConfig)
      }

      fullResponse.value = incoming
      lastSavedConfig.value = klona(incomingConfig)
      lastChecksum.value = incoming.checksum
      runWithoutTeamAutosave(() => {
        currentTeam.value = klona(incoming.team)
      })
      teamMembers.value = klona(incoming.members)

      if (!incoming.draft) {
        publishState.value = PublishState.Published
        isDirty.value = false
      } else {
        publishState.value = PublishState.Saved
        isDirty.value = hasConfigChanges(incoming.draft, incoming.stable)
      }

      // Repair config on load: fix isQuestion flags and reconcile flow splits
      // without treating this background normalization as a user edit. If these
      // legacy repairs materialize changes on first load, they should ride along
      // with the next real edit instead of writing the whole draft during startup.
      if (config.value) {
        repairedAdoption = null
        void nextTick(() => {
          runWithoutAutosave(() => {
            repairQuestions()
            repairFlowSplits()
          })
          repairedAdoption = config.value ? klona(toRaw(config.value)) : null
        })
      }
    },
    { immediate: true }
  )

  watch(
    config,
    (newConfig) => {
      // Skip auto-save for changes that did not originate from a user edit:
      // wholesale server/MCP adoption after a refetch (isApplyingRemoteConfig),
      // passive open-time normalizations (isNormalizing), and accessedAt- or
      // modifiedAt-only churn all count as non-user changes here. Only meaningful diffs should
      // mark the tab as actively editing or POST a draft during startup.
      if (
        newConfig &&
        shouldAutosaveConfigChange({
          hasMeaningfulChanges: hasConfigChanges(newConfig, lastSavedConfig.value),
          isApplyingRemoteConfig: isApplyingRemoteConfig.value,
          isNormalizing: isNormalizing.value
        })
      ) {
        markLocalEditActive()
        void debouncedUpdateConfig(newConfig)
      }
    },
    { deep: true }
  )

  const saveSnapshot = async (description: string) => {
    if (!config.value) {
      return
    }
    try {
      const data = await updateConfigAsync({
        config: config.value,
        snapshotDescription: description
      })
      void queryClient.invalidateQueries({ queryKey: ['snapshots'] })
      // Invalidate snapshotConfig
    } catch (err) {
      // The update mutation already reports save failures through its onError notification.
    }
  }
  const saveCurrentDraft = (): Promise<boolean> => {
    if (savingCurrentDraft) {
      return savingCurrentDraft
    }
    savingCurrentDraft = (async () => {
      flushBufferedLocalEdits()
      await nextTick()

      await until(isUpdatingConfig).toBe(false)

      if (remoteRefetch.isDeferred()) {
        if (hasLocalEditsToProtect()) {
          promptRemoteChange()
        }
        return false
      }

      while (config.value && hasUnsavedLocalChanges()) {
        if (remoteRefetch.isDeferred()) {
          promptRemoteChange()
          return false
        }
        const response = await updateConfigAsync({ config: config.value })
        if (response.data?.info?.stale_save_rejected) {
          return false
        }
        isDirty.value = true
        lastMeaningfulSaveTime.value = Date.now()
        flushBufferedLocalEdits()
        await nextTick()
      }
      return true
    })().finally(() => {
      savingCurrentDraft = null
    })
    return savingCurrentDraft
  }

  const {
    mutate: updateConfig,
    mutateAsync: updateConfigAsync,
    isPending: isUpdatingConfig
  } = useMutation({
    mutationFn: (variables: { config: Config; snapshotDescription?: string }) => {
      // Capture an immutable snapshot of what we're actually sending to the backend.
      // This same object is used for both the HTTP body and updating lastSavedConfig,
      // ensuring they can never diverge. (ENG-966: fixes lastSavedConfig drift)
      const payload = klona(toRaw(variables.config))
      const seq = ++saveSeq
      return api
        .post(`/api/${currentTeamPid.value}/configs/draft`, {
          data: payload,
          checksum: lastChecksum.value,
          tabId,
          snapshot_description: variables.snapshotDescription
        })
        .then((res) => ({ ...res.data, _savedSnapshot: payload, _seq: seq }))
    },
    onSuccess: (response) => {
      if (response.data.info?.stale_save_rejected) {
        // The backend rejected our save: the server's draft moved ahead of our
        // baseline (a foreign MCP/Righty/other-tab write). We do NOT rebase and
        // do NOT re-POST this stale whole-config payload — that was the
        // ENG-830/966 machinery that silently overwrote MCP changes, removed in
        // ENG-1066. Apply the one simple rule (resolveStaleOrForeignChange):
        //  - no unsaved local edits → refetch and adopt server/MCP state.
        //  - unsaved local edits    → prompt to reload; the coordinator parks
        //    the refetch (shouldHold) so we never clobber either side. No merge.
        lastChecksum.value = response.data.checksum
        // Buffered inputs may hold user edits that have not hit config yet.
        // Flush them before resolving refresh vs prompt so a stale rejection
        // cannot silently adopt server state and then let the pending buffer
        // overwrite it with stale local text.
        flushBufferedLocalEdits()
        if (
          resolveStaleOrForeignChange({
            hasUnsavedLocalChanges: hasLocalEditsToProtect()
          }) === 'prompt'
        ) {
          promptRemoteChange()
        }
        remoteRefetch.request()
        void nextTick(remoteRefetch.flush)
        return
      }
      // Always update the checksum — even for out-of-order responses — so the
      // next save uses the latest checksum the backend has seen from us. Skipping
      // this can cause unnecessary stale-save rejections. (ENG-966)
      lastChecksum.value = response.data.checksum
      // Only update lastSavedConfig from the latest save — ignore out-of-order
      // responses from overlapping mutations. (ENG-966: save sequence guard)
      if (response._seq < saveSeq) {
        return
      }
      // Use the snapshot captured at mutation time, NOT config.value which may have
      // advanced since the request was sent. (ENG-966: core fix)
      lastSavedConfig.value = response._savedSnapshot
      repairedAdoption = null
      if (config.value) {
        applyModifiedAtStamps(config.value, response.data.modified_at)
      }
      savesMade.value += 1
      publishState.value = PublishState.Dirty
      void nextTick(remoteRefetch.flush)
    },
    onError: (error) => {
      console.error('Failed to save config:', error)
      const notificationStore = useNotificationStore()
      const responseData = isAxiosError(error) ? error.response?.data : undefined
      // A running question A/B test locks its wording server-side; the 422
      // names the question in errors.config, which is more useful than the
      // generic validation summary.
      const errorMessage = responseData?.errors?.config?.[0] ?? responseData?.message
      notificationStore.addError(
        errorMessage || 'Failed to save changes. Your recent changes may not have been saved.',
        true
      )
    }
  })

  const debouncedUpdateConfig = useDebounceFn((newConfig: Config) => {
    if (savingCurrentDraft || rightyRefetch || newConfig !== config.value) {
      return
    }
    // Skip auto-save while Righty is actively streaming (making tool calls).
    // Righty's MCP tools mutate config on the backend and broadcast via Pusher.
    // If we save stale frontend data during tool execution, it overwrites those mutations.
    try {
      const rightyStore = useRightyStore()
      if (rightyStore.isStreaming) {
        return
      }
    } catch {
      // rightyStore may not be initialized yet — proceed with save
    }

    // Never autosave when we know the server's draft has moved ahead of our
    // baseline. A deferred remote refetch means a real foreign DraftConfig
    // Updated (or a stale-save rejection) is parked — POSTing now would
    // overwrite the newer server/MCP state with our stale config. The
    // server-side stale-save rejection is the backstop; this is the belt.
    // Only nag the user to reload when there are GENUINE unsaved edits that
    // adopting the server state would discard; otherwise stay silent and let
    // the parked refetch adopt server state once the editor goes idle.
    // (ENG-1066: the webapp never POSTs a config behind the server's draft.)
    if (remoteRefetch.isDeferred()) {
      if (hasUnsavedLocalChanges()) {
        promptRemoteChange()
      }
      return
    }

    const from = JSON.stringify(toRaw(newConfig))
    const to = JSON.stringify(toRaw(lastSavedConfig.value))

    // Check if there are meaningful changes using composable
    const hasMeaningfulChanges = hasConfigChanges(newConfig, lastSavedConfig.value)

    if (to && from !== to && hasMeaningfulChanges) {
      updateConfig({ config: newConfig })
      isDirty.value = true
      lastMeaningfulSaveTime.value = Date.now()
    } else {
      // Ignore accessedAt-only churn entirely. Opening a resource must not POST
      // the full config back to the server or create a foreign-change race for
      // other tabs that are still starting up.
      remoteRefetch.flush()
    }
  }, 2000) // 2 second debounce time

  const PUBLISH_CHECK_INTERVAL_MS = 3000
  const PUBLISH_CHECK_LIMIT_MS = 75_000

  // Polls without adopting: the guarded refetch at the end protects edits made meanwhile.
  const settleTimedOutPublish = async (
    teamPid: string,
    publishedBefore: number | null | undefined,
    hostedDigest: string | null
  ) => {
    const notificationStore = useNotificationStore()
    notificationStore.add({
      message: 'Publishing is taking longer than expected. Checking whether it went live…',
      type: 'info'
    })
    let outcome: 'live' | 'pagesPending' | 'failed' | 'unknown' = 'unknown'
    const deadline = Date.now() + PUBLISH_CHECK_LIMIT_MS
    while (Date.now() < deadline) {
      await new Promise((resolve) => {
        setTimeout(resolve, PUBLISH_CHECK_INTERVAL_MS)
      })
      if (String(currentTeamPid.value) !== teamPid) {
        return
      }
      try {
        const { data } = await api.get<CurrentConfigResponse>(`/api/${teamPid}/configs/current`)
        if (!data.publishing) {
          outcome = (data.publishedConfigId ?? null) === (publishedBefore ?? null) ? 'failed' : 'live'
          break
        }
      } catch {
        // Keep checking until the deadline.
      }
    }
    if (String(currentTeamPid.value) !== teamPid) {
      return
    }
    // The tag goes live before hosted pages publish, so a live tag says nothing about the pages in the same publish.
    if (outcome === 'live' && hostedDigest !== null) {
      try {
        if ((await fetchHostedSite(teamPid)).changes.length > 0) {
          outcome = 'pagesPending'
        }
      } catch {
        outcome = 'unknown'
      }
      if (String(currentTeamPid.value) !== teamPid) {
        return
      }
    }
    requestRemoteRefetch()
    if (outcome === 'live') {
      notificationStore.addNotification('Your changes are live.')
    } else if (outcome === 'pagesPending') {
      notificationStore.addError(
        "Your site changes are live, but your page changes didn't publish. Please publish again.",
        true
      )
    } else if (outcome === 'failed') {
      notificationStore.addError("Publishing didn't finish. Please publish again.", true)
    } else {
      notificationStore.addError(
        "We couldn't confirm whether your changes went live. Check Publish history before publishing again.",
        true
      )
    }
  }

  const publishConfigMutation = useMutation({
    mutationFn: ({
      config,
      message,
      hostedDigest
    }: {
      config: Config
      message?: string
      notify: boolean
      hostedDigest: string | null
    }): Promise<PublishResponse> =>
      api
        .post(`/api/${currentTeamPid.value}/configs/publish`, {
          data: config,
          checksum: lastChecksum.value,
          tabId,
          publish_message: message,
          hosted_digest: hostedDigest
        })
        .then((res) => res.data),
    onMutate: ({ hostedDigest }) => ({
      teamPid: String(currentTeamPid.value),
      publishedBefore: fullResponse.value?.publishedConfigId,
      hostedDigest
    }),
    onSuccess: (_response, { notify }) => {
      savesMade.value += 1

      // The first-publish card prompt confirms the publish itself, so its caller skips this toast.
      if (notify) {
        useNotificationStore().addNotification('Successfully published your changes!')
      }
      // All other state updates handled by the config (re)fetch watcher
    },
    onError: (error, _variables, context) => {
      const response = isAxiosError(error) ? error.response : undefined
      // Don't show notification for 402 (requires subscription) — handled by PublishButton
      if (response?.status === 402 && response.data?.requires_subscription) {
        return
      }
      // The PublishBar turns a stale hosted review into a refresh prompt instead.
      if (response?.status === 409 && response.data?.code === 'hosted_digest_mismatch') {
        return
      }
      const notificationStore = useNotificationStore()
      // The edge gives up on a long publish while the server keeps going and usually finishes it.
      // Adopt the server's real state: a finished publish clears the bar, and a failed one
      // broadcasts DraftConfigUpdated, whose refetch restores the draft.
      if (isPublishStillRunningError(error) && context) {
        void settleTimedOutPublish(context.teamPid, context.publishedBefore, context.hostedDigest)
        return
      }
      // A 5xx body only says "Server Error"; a 4xx message is written for the customer.
      const errorMessage =
        (response && response.status < 500 && response.data?.message) || 'Failed to publish changes. Please try again.'
      notificationStore.addError(errorMessage, true)
    }
  })

  const publish = async (
    message?: string,
    { notify = true, hostedDigest = null }: { notify?: boolean; hostedDigest?: string | null } = {}
  ): Promise<PublishResponse | undefined> => {
    if (!config.value) {
      return undefined
    }
    const isFirstPublish = !currentTeam.value?.last_published_at
    const response = await publishConfigMutation.mutateAsync({ config: config.value, message, notify, hostedDigest })
    // Snapshot the published config as the merge base so the post-publish refetch
    // doesn't stomp any edits made between clicking Publish and the refetch landing.
    lastSavedConfig.value = klona(toRaw(config.value))
    repairedAdoption = null
    // Wait for the refetch to complete so isDirty is properly recalculated
    await configQuery.refetch()
    void recordAnalyticsEvent(AnalyticsEventTypes.PUBLISHED)
    if (isFirstPublish) {
      void recordAnalyticsEvent(AnalyticsEventTypes.FIRST_PUBLISH)
    }
    return response
  }

  const validateForPublish = (): PublishValidationError[] => {
    const errors: PublishValidationError[] = []
    const integrationStore = useIntegrationStore()
    const widgetStore = useWidgetStore()
    const offerStore = useOfferStore()
    const offerValidation = classifyOfferPublishability({
      offers: offerStore.optinOffers,
      hasIntegration: !!integrationStore.integratedPartner,
      offerFlows: offerUsedBy,
      widgetsForFlowId: (flowId) => widgetStore.getWidgetsForOfferFunnel(flowId)
    })

    if (offerValidation.missingIntegration.length > 0) {
      const problematicForms = offerValidation.missingIntegration.map((offer) => offer.name || 'Unnamed form')

      const formList =
        problematicForms.length <= 3
          ? problematicForms.join(', ')
          : `${problematicForms.slice(0, 3).join(', ')}, and ${problematicForms.length - 3} more`

      errors.push({
        message: `The following in-use forms require a native email marketing / CRM integration before they can publish: ${formList}. Connect an integration or switch those forms to a webhook action.`,
        references: []
      })
    }

    if (offerValidation.invalidConfiguration.length > 0) {
      const problematicForms = offerValidation.invalidConfiguration.map((offer) => offer.name || 'Unnamed form')

      const formList =
        problematicForms.length <= 3
          ? problematicForms.join(', ')
          : `${problematicForms.slice(0, 3).join(', ')}, and ${problematicForms.length - 3} more`

      const configurationTarget = integrationStore.integratedPartner
        ? integrationStore.integratedPartner.label
        : 'their selected action'

      errors.push({
        message: `The following forms are in use but not fully configured to submit to ${configurationTarget}: ${formList}. Edit each form's Action tab and complete the required settings.`,
        references: []
      })
    }

    if (!integrationStore.integratedPartner && configAsString.value.includes('espplaceholder')) {
      errors.push({
        message: `You have Flows or segments in place that currently reference a visitor's contact status (i.e. anonymous or known contact), but you haven't yet set up an integration with your email marketing platform or CRM.`,
        references: []
      })
    }

    // Validate Cloudflare Turnstile keys if set
    const siteKey = currentTeam.value?.recaptcha_key
    const secretKey = currentTeam.value?.recaptcha_secret
    const hasBadSiteKey = siteKey && !isValidTurnstileSiteKey(siteKey)
    const hasBadSecretKey = secretKey && !isValidTurnstileSecretKey(secretKey)

    if (hasBadSiteKey || hasBadSecretKey) {
      errors.push({
        message: `Your Cloudflare Turnstile configuration appears to be invalid. Valid keys start with a digit followed by "x". You can fix this in your project settings under Bot Protection.`,
        references: []
      })
    }

    // Validate sync field conflicts (Other freeform vs Structured sync using same custom field)
    const questions = config.value?.rightAsk?.questions || []
    const dimensions = config.value?.dimensions || []

    const conflictingQuestions = questions
      .filter((question) => {
        // Only check questions that have "Other" sync configured
        const otherField = question.otherOptions?.syncToCustomField
        if (!otherField) {
          return false
        }

        // Find the segment group for this question
        const segmentGroup = dimensions.find((d) => d.id === question.dimensionId)
        if (!segmentGroup) {
          return false
        }

        // Check if structured sync uses the same field
        return segmentGroup.syncToCustomField === otherField
      })
      .map((q) => {
        const segmentGroup = dimensions.find((d) => d.id === q.dimensionId)
        return segmentGroup?.name || 'Unnamed question'
      })

    if (conflictingQuestions.length > 0) {
      const questionList =
        conflictingQuestions.length <= 3
          ? conflictingQuestions.join(', ')
          : `${conflictingQuestions.slice(0, 3).join(', ')}, and ${conflictingQuestions.length - 3} more`

      errors.push({
        message: `The following questions have the same custom field configured for both "Other" freeform responses and structured segment sync: ${questionList}. Using the same field for both will cause data conflicts. Please use different fields for each sync type.`,
        references: []
      })
    }

    return errors
  }

  const associatedWebsites = computed(() => fullResponse.value?.teams)

  return {
    publishState,
    isDirty,
    lastMeaningfulSaveTime,
    currentTeamPid,
    fullResponse,
    owner,
    config,
    configAsString,
    currentTeam,
    lastChecksum,
    savesMade,
    isLoading: configQuery.isLoading,
    isFetching: configQuery.isFetching,
    isError: configQuery.isError,
    configQuery,
    saveSnapshot,
    saveCurrentDraft,
    deleteSnapshotMutation,
    snapshots,
    isUpdatingConfig,
    publish,
    validateForPublish,
    teamMembers,
    associatedWebsites,
    debouncedUpdateConfig,
    isApplyingRemoteTeam,
    // Coordinator-routed refetch for caller-driven local actions (Righty
    // streaming-end after MCP mutations, Righty undo). Always use this in
    // preference to `configQuery.refetch()` for in-team refetches that race
    // with local edits — see ENG-1066 Greptile follow-up.
    requestRemoteRefetch,
    requestRightyRefetch,
    // Run passive open-time normalizations (repairSegmentGroup, load-time
    // repairs) without the deep watcher persisting them as a spurious draft
    // (ENG-1063). Last-accessed stamping no longer touches config at all —
    // see useResourceAccessTimes (localStorage-backed).
    runWithoutAutosave,
    // Run whole-team server/store adoption without the teamStore deep watch
    // POSTing the replacement object back and clobbering server-only fields.
    runWithoutTeamAutosave,
    // Expose this tab's id so other request paths (Righty inline AI, etc.) can
    // include it on backend mutations and the resulting DraftConfigUpdated
    // broadcast can be self-suppressed instead of clobbering local edits (ENG-1020).
    tabId: () => tabId
  }
})
